import { Logger } from "@/lib/logger";
import { API_BASE_URL, fetchWithRetry } from "@/services/api";
import { revokePushTokenOnLogout } from "@/lib/push/messaging";
import type { AuthUser, UserRole } from "@/types";

const logger = new Logger("AuthService");

// ── Response types ───────────────────────────────────────────────────────────

interface LoginResponse {
  token_type: string;
  access_token: string;
  user: Pick<AuthUser, "id" | "uuid" | "user_code" | "first_name" | "last_name" | "email">;
  must_change_password: boolean;
  temporary_password_expires_at: string | null;
}

interface ChangePasswordPayload {
  current_password: string;
  password: string;
  password_confirmation: string;
}

interface ChangePasswordResponse {
  message: string;
}

interface MeResponse {
  user: AuthUser;
}

// ── Role mapping ─────────────────────────────────────────────────────────────

export function mapApiRolesToAppRole(roles: string[]): UserRole {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("teacher")) return "teacher";
  if (roles.includes("student")) return "student";
  // "parent", "family", or any other → family dashboard
  return "family";
}

// ── API calls ────────────────────────────────────────────────────────────────

const JSON_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
} as const;

function authHeaders(token: string) {
  return { ...JSON_HEADERS, Authorization: `Bearer ${token}` };
}

export class AuthApiError extends Error {
  status: number;
  fieldErrors: Record<string, string[]>;

  constructor(status: number, message: string, fieldErrors: Record<string, string[]> = {}) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as { message?: string }).message ?? `${res.status} ${res.statusText}`;
    const fieldErrors = (body as { errors?: Record<string, string[]> }).errors ?? {};
    const apiError = new AuthApiError(res.status, message, fieldErrors);
    if (res.status >= 500) {
      logger.error(`Auth API error ${res.status} on ${res.url}`, apiError);
    } else {
      logger.warn(`Auth API error ${res.status} on ${res.url}`, apiError);
    }
    throw apiError;
  }
  return res.json() as Promise<T>;
}

export async function loginApi(email: string, password: string): Promise<LoginResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/login`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ email, password }),
  });
  return handleResponse<LoginResponse>(res);
}

export async function childLoginApi(username: string, pin: string): Promise<LoginResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/child-login`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ username, pin }),
  });
  return handleResponse<LoginResponse>(res);
}

export async function getMeApi(token: string): Promise<MeResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/me`, {
    headers: authHeaders(token),
  });
  return handleResponse<MeResponse>(res);
}

export async function changePasswordApi(
  token: string,
  payload: ChangePasswordPayload,
): Promise<ChangePasswordResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/change-password`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return handleResponse<ChangePasswordResponse>(res);
}

// ── Forgot password ─────────────────────────────────────────────────────────

interface ForgotPasswordResponse {
  message: string;
}

export async function forgotPasswordApi(email: string): Promise<ForgotPasswordResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/forgot-password`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ email }),
  });
  return handleResponse<ForgotPasswordResponse>(res);
}

// ── Reset password ──────────────────────────────────────────────────────────

interface ResetPasswordPayload {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
}

interface ResetPasswordResponse {
  message: string;
}

export async function resetPasswordApi(
  payload: ResetPasswordPayload,
): Promise<ResetPasswordResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/reset-password`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  return handleResponse<ResetPasswordResponse>(res);
}

// ── Email verification ───────────────────────────────────────────────────────

interface VerifyEmailResponse {
  message: string;
  user: {
    id: string;
    uuid: string;
    first_name: string;
    last_name: string;
    email: string;
    email_verified_at: string;
  };
}

export async function verifyEmailApi(
  id: string,
  hash: string,
  queryParams?: URLSearchParams,
): Promise<VerifyEmailResponse> {
  const qs = queryParams?.toString();
  const url = `${API_BASE_URL}/verify-email/${id}/${hash}${qs ? `?${qs}` : ""}`;
  const res = await fetchWithRetry(url, {
    headers: { Accept: "application/json" },
  });
  return handleResponse<VerifyEmailResponse>(res);
}

interface ResendVerificationResponse {
  message: string;
}

export async function resendVerificationEmailApi(
  email: string,
): Promise<ResendVerificationResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/email/verification-notification`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ email }),
  });
  return handleResponse<ResendVerificationResponse>(res);
}

// ── Register ────────────────────────────────────────────────────────────────

interface RegisterPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  user_type: string;
  address?: string;
}

interface RegisterResponse {
  message: string;
  token_type: string;
  access_token: string;
  user: {
    id: number;
    uuid: string;
    user_code: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url: string | null;
    email_verified_at: string | null;
    is_active: boolean;
    roles?: string[];
    created_at: string;
    updated_at: string;
  };
}

export async function registerApi(payload: RegisterPayload): Promise<RegisterResponse> {
  const res = await fetchWithRetry(`${API_BASE_URL}/v1/auth/register`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  return handleResponse<RegisterResponse>(res);
}

export async function logoutApi(token: string): Promise<void> {
  // Révoque le jeton push web de cet appareil **avant** d'invalider la session : passé ce point le
  // token Sanctum capturé en argument ne sert plus à rien, et `useAuthStore.getState().token` (dont
  // dépendrait `fetchJson`) est déjà remis à `null` par l'appelant au même tick. Le back ne
  // supprime pas ce jeton lui-même au logout (il ne le connaît pas à ce moment) — cf.
  // `docs/PWA_PUSH_NOTIFICATIONS.md` §3.3. Best-effort et non bloquant : un échec ici ne doit
  // jamais retarder la déconnexion.
  void revokePushTokenOnLogout(token);

  await fetch(`${API_BASE_URL}/v1/auth/logout`, {
    method: "POST",
    headers: authHeaders(token),
  }).catch(() => {
    // Best-effort: ignore network errors on logout
  });
}
